/**
 * Shared HD slide primitives used by Hymn & Bible live-display screens.
 * Keep all cinematic visual pieces (backdrop, ornate badge, gold verse
 * numbers, gold labels, divider, footer pill) in one place so Hymn and
 * Bible slides stay visually consistent.
 */
import React, { useRef, useEffect, useState, useCallback } from 'react';

/* -------- Auto-fit text box with optional renderInner render-prop -------- */
export const FitText = ({ text, maxSize = 60, minSize = 14, className = '', style = {}, renderInner }) => {
  const textRef = useRef(null);
  const wrapRef = useRef(null);
  const [fontSize, setFontSize] = useState(minSize);

  const fitText = useCallback(() => {
    const el = textRef.current;
    const wrap = wrapRef.current;
    if (!el || !wrap) return;
    const wrapH = wrap.clientHeight;
    const wrapW = wrap.clientWidth;
    if (wrapH < 50 || wrapW < 50) return;
    let lo = minSize, hi = maxSize, best = minSize;
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      el.style.fontSize = `${mid}px`;
      if (el.scrollHeight <= wrapH && el.scrollWidth <= wrapW) {
        best = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    setFontSize(best);
    el.style.fontSize = `${best}px`;
  }, [maxSize, minSize]);

  useEffect(() => {
    const timers = [setTimeout(fitText, 50), setTimeout(fitText, 200), setTimeout(fitText, 500)];
    window.addEventListener('resize', fitText);
    let observer;
    if (wrapRef.current && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => fitText());
      observer.observe(wrapRef.current);
    }
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', fitText);
      if (observer) observer.disconnect();
    };
  }, [fitText, text]);

  return (
    <div ref={wrapRef} style={{ width: '100%', height: '100%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p ref={textRef} className={className} style={{ ...style, fontSize: `${fontSize}px`, maxWidth: '100%', margin: 0, textAlign: 'center' }}>
        {renderInner ? renderInner() : text}
      </p>
    </div>
  );
};

/* -------- Split leading "N." (or "N:") off a string -------- */
export const splitVerseNumber = (raw) => {
  const s = (raw || '').trim();
  const m = s.match(/^(\d+)[.:)]\s+([\s\S]*)$/);
  if (m) return { number: `${m[1]}.`, body: m[2] };
  return { number: '', body: s };
};

/* -------- Gold pill flanker (diamond + fading line) -------- */
const BadgeFlanker = ({ direction }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexDirection: direction === 'right' ? 'row-reverse' : 'row' }}>
    <div style={{ width: '10px', height: '10px', transform: 'rotate(45deg)', background: 'linear-gradient(135deg, #fde68a, #f59e0b)', boxShadow: '0 0 10px rgba(251,191,36,0.7)' }} />
    <div style={{ width: '70px', height: '2px', background: 'linear-gradient(90deg, rgba(251,191,36,0.85), rgba(251,191,36,0))' }} />
  </div>
);

/* -------- Ornate gold pill used for the top badge (HYMN N, Genesis 5, etc.) -------- */
export const OrnateGoldBadge = ({ children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
    <BadgeFlanker direction="left" />
    <div
      className="rounded-2xl px-7 py-2.5"
      style={{
        background: 'linear-gradient(180deg, #fde68a 0%, #f59e0b 55%, #b45309 100%)',
        border: '3px solid rgba(253, 224, 138, 0.9)',
        boxShadow: '0 0 0 2px rgba(16,24,48,0.55) inset, 0 8px 28px rgba(251,191,36,0.45), 0 0 40px rgba(251,191,36,0.25)',
      }}
    >
      <span
        className="font-black tracking-[0.18em]"
        style={{
          color: '#1a1a1a',
          fontSize: 'clamp(20px, 3vh, 38px)',
          textShadow: '0 1px 0 rgba(255,255,255,0.45)',
          fontFamily: '"Cinzel", "Playfair Display", Georgia, serif',
          whiteSpace: 'nowrap',
        }}
      >
        {children}
      </span>
    </div>
    <BadgeFlanker direction="right" />
  </div>
);

/* -------- Gold language label (ENGLISH / YORUBA) with diamond dashes -------- */
const LabelDash = ({ mirror = false }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexDirection: mirror ? 'row-reverse' : 'row' }}>
    <div style={{ width: '40px', height: '1.5px', background: 'linear-gradient(90deg, rgba(251,191,36,0), rgba(251,191,36,0.85))' }} />
    <div style={{ width: '6px', height: '6px', transform: 'rotate(45deg)', background: '#fde68a', boxShadow: '0 0 8px rgba(251,191,36,0.7)' }} />
  </div>
);

export const GoldLabel = ({ children }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '0.8vh' }}>
    <LabelDash />
    <span
      className="font-black tracking-[0.35em]"
      style={{
        color: '#fde68a',
        fontSize: 'clamp(12px, 1.45vh, 16px)',
        textShadow: '0 2px 6px rgba(0,0,0,0.6)',
        fontFamily: '"Cinzel", "Playfair Display", Georgia, serif',
      }}
    >
      {children}
    </span>
    <LabelDash mirror />
  </div>
);

/* -------- Central vertical gold divider with cross (bilingual split) -------- */
export const CentralDivider = () => (
  <div style={{
    position: 'absolute', left: '50%', top: '2vh', bottom: '2vh',
    width: '2px', transform: 'translateX(-50%)',
    background: 'linear-gradient(180deg, rgba(251,191,36,0) 0%, rgba(251,191,36,0.55) 12%, rgba(251,191,36,0.55) 88%, rgba(251,191,36,0) 100%)',
    boxShadow: '0 0 12px rgba(251,191,36,0.35)',
    pointerEvents: 'none',
  }}>
    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '18px', height: '18px', color: '#fde68a' }}>
      <svg viewBox="0 0 24 24" width="18" height="18" style={{ filter: 'drop-shadow(0 0 6px rgba(251,191,36,0.8))' }}>
        <path d="M12 2l0 20M5 9l14 0" stroke="#fde68a" strokeWidth="2.2" fill="none" strokeLinecap="round" />
        <circle cx="12" cy="9" r="1.6" fill="#fde68a" />
      </svg>
    </div>
  </div>
);

/* -------- Ornate gold footer pill (VERSE N OF M) -------- */
export const GoldFooterPill = ({ children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
    <svg viewBox="0 0 240 20" width="220" height="14" style={{ opacity: 0.9 }} aria-hidden>
      <path d="M8 14 C 50 2, 90 18, 120 8 C 150 -2, 190 14, 232 4" stroke="rgba(251,191,36,0.75)" strokeWidth="1.3" fill="none" strokeLinecap="round" />
      <circle cx="120" cy="8" r="2.5" fill="#fde68a" />
    </svg>
    <div
      className="rounded-lg px-5 py-1"
      style={{
        border: '1.5px solid rgba(251,191,36,0.7)',
        background: 'linear-gradient(180deg, rgba(16,32,74,0.75), rgba(6,12,30,0.75))',
        boxShadow: '0 0 16px rgba(251,191,36,0.18)',
      }}
    >
      <span
        className="tracking-[0.25em] font-bold"
        style={{
          color: '#fde68a',
          fontSize: 'clamp(10px, 1.2vh, 13px)',
          textShadow: '0 1px 3px rgba(0,0,0,0.7)',
          fontFamily: '"Cinzel", "Playfair Display", Georgia, serif',
        }}
      >
        {children}
      </span>
    </div>
  </div>
);

/* -------- Divine radial-light backdrop -------- */
export const DivineBackdrop = () => (
  <>
    <div style={{
      position: 'absolute', inset: 0,
      background:
        'radial-gradient(ellipse 60% 75% at 50% -10%, rgba(96,165,250,0.35) 0%, rgba(30,58,138,0.18) 40%, rgba(10,15,26,0) 75%),' +
        'radial-gradient(ellipse 80% 60% at 50% 120%, rgba(212,168,75,0.18) 0%, transparent 55%),' +
        'linear-gradient(180deg, #0b1f4a 0%, #081634 55%, #030814 100%)',
    }} />
    <div style={{
      position: 'absolute', inset: 0, overflow: 'hidden',
      maskImage: 'linear-gradient(180deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0.35) 55%, transparent 80%)',
      WebkitMaskImage: 'linear-gradient(180deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0.35) 55%, transparent 80%)',
    }}>
      <div style={{
        position: 'absolute', left: '50%', top: '-20%', transform: 'translateX(-50%)',
        width: '140vw', height: '120vh',
        background:
          'conic-gradient(from 180deg at 50% 0%, rgba(147,197,253,0) 0deg, rgba(147,197,253,0.10) 6deg, rgba(191,219,254,0) 12deg, rgba(147,197,253,0.10) 18deg, rgba(147,197,253,0) 24deg, rgba(147,197,253,0.08) 30deg, rgba(147,197,253,0) 36deg)',
        animation: 'hdRays 18s linear infinite',
        opacity: 0.55,
      }} />
    </div>
    <style>{`@keyframes hdRays { from { transform: translateX(-50%) rotate(0deg); } to { transform: translateX(-50%) rotate(360deg); } }`}</style>
  </>
);

/* -------- Shared font styles for bold-halo body and gold verse-number -------- */
export const BODY_FONT_STYLE = {
  fontWeight: 900,
  lineHeight: 1.25,
  textShadow:
    '2px 2px 0 rgba(0,0,0,0.95), -2px 2px 0 rgba(0,0,0,0.95), 2px -2px 0 rgba(0,0,0,0.95), -2px -2px 0 rgba(0,0,0,0.95), 0 4px 16px rgba(0,0,0,0.9), 0 2px 4px rgba(0,0,0,0.95)',
  letterSpacing: '-0.005em',
  fontFamily: '"Inter", "Helvetica Neue", system-ui, sans-serif',
};

export const GOLD_NUM_STYLE = {
  color: '#fde68a',
  fontWeight: 900,
  marginRight: '0.55em',
  textShadow:
    '2px 2px 0 rgba(0,0,0,0.9), -2px 2px 0 rgba(0,0,0,0.9), 2px -2px 0 rgba(0,0,0,0.9), -2px -2px 0 rgba(0,0,0,0.9), 0 0 16px rgba(251,191,36,0.6), 0 3px 10px rgba(0,0,0,0.7)',
};
